import { memo, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferBadge from 'components/Common/OfferBadge'

import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import HeroBedbankOfferDetails from './components/HeroBedbankOfferDetails'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import Group from 'components/utils/Group'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'

const Root = styled.div`
  display: grid;
  position: relative;
  height: 100%;
  grid-template-rows: min-content 1fr;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 67% 1fr;
    grid-template-rows: 1fr;
  }
`

const MainImageSegment = styled.div`
  aspect-ratio: 3 / 2;

  ${mediaQueryUp.tablet} {
    aspect-ratio: 16 /9;
  }

  ${mediaQueryUp.desktop} {
    aspect-ratio: unset;
  }
`

interface Props {
  offer: App.BedbankOfferSummary
  eagerLoadFirstImage?: boolean
  offerUrl: string
}

const imageParams: ImageParams = {
  mobileAspectRatio: '3:2',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '19:9',
  mobileWidth: '400px',
  desktopWidth: '67vw',
  largeDesktopWidth: '764px',
  quality: 'good',
}

function HeroBedbankOfferTile(props: Props) {
  const {
    offer,
    eagerLoadFirstImage,
  } = props

  const isMobile = useIsMobileDevice()
  // Swiping through the offer tiles is tough when the image carousel can also swipe. So turn off the image carousel for mobile devices
  const images = useMemo(() => isMobile ? [offer.images[0]] : offer.images, [isMobile, offer.images])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  return (
    <ProductPaletteProvider palette="default">
      <Root>
        <MainImageSegment>
          <ImageCarousel
            topRight={<Group direction="horizontal" gap={4}>
              <TripGuard>
                <CSSBreakpoint max="tablet">
                  <BookmarkButton offer={offer} />
                </CSSBreakpoint>
              </TripGuard>
              {offer.badge && <OfferBadge badge={offer.badge} />}
            </Group>}
            images={images}
            imageParams={imageParams}
            eagerLoadFirstImage={eagerLoadFirstImage}
            onImageLoad={handleImageLoaded}
          />
        </MainImageSegment>
        <HeroBedbankOfferDetails offer={offer} />
      </Root>
    </ProductPaletteProvider>
  )
}

export default memo(HeroBedbankOfferTile)
